import { zodSchema } from 'ai';
import type { Action, ActionConstructor, ApplicableContext } from './types';

/**
 * Frontend actions Coco can call, expressed as ad-hoc interruptable tools.
 *
 * The catalog is rebuilt from scratch each time the model asks, out of the
 * actions that are `applicable()` at that moment, so an action only reaches it
 * while the page that provides it is mounted.
 */

/** Namespace for frontend-action tools inside the reserved `client/` prefix. */
const FE_ACTION_TOOL_KEY_PREFIX = 'client/fe-action/';

/**
 * Key for the tool that asks this browser what the page can do.
 *
 * Deliberately outside {@link FE_ACTION_TOOL_KEY_PREFIX}: it asks about actions
 * rather than being one, and the renderer that answers it is not the renderer
 * that runs them.
 */
export const FE_ACTION_LIST_TOOL_KEY = 'client/frontend-actions';

/**
 * Declared on every submission, so the model can always ask what is on screen.
 *
 * Takes no arguments and answers with everything applicable: one page offers a
 * handful of actions, each already carrying a description written for a model,
 * so filtering here would only be the browser guessing at what the model meant.
 * It is better placed to choose than any ranking this side could do.
 *
 * There is no server-side implementation — that is the point. Calling it
 * suspends the run and the browser answers, and because nothing defines it back
 * there it cannot be turned up by tool search or attached to a conversation with
 * nobody watching.
 */
export const frontendActionListToolDefinition =
  (): FrontendActionDefinition => ({
    toolKey: FE_ACTION_LIST_TOOL_KEY,
    name: 'List frontend actions',
    description:
      'List everything you can do on the page the user is looking at right now — for example running a query against a dataset preview they have open, or sharing what is in front of them. Each one comes back callable straight away, so check here before assuming you cannot act on what is on screen.',
    inputSchema: { type: 'object', properties: {}, required: [] },
  });

/**
 * Ids become tool keys, and tool keys survive a round trip through the LLM's
 * naming rules that maps `_` to `/`. An underscore would silently reshape the
 * key into extra path segments and the call would come back unrecognised, so
 * ids are restricted to what survives unchanged.
 */
const AGENT_TOOL_ID_PATTERN = /^[a-zA-Z0-9][a-zA-Z0-9-]*$/;

/** Shape of an entry in the published catalog; mirrors `InterruptableToolDefinition`. */
export type FrontendActionDefinition = {
  toolKey: string;
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
};

const feActionToolKey = (id: string): string =>
  `${FE_ACTION_TOOL_KEY_PREFIX}${id}`;

export const isFeActionToolKey = (toolKey: string): boolean =>
  toolKey.startsWith(FE_ACTION_TOOL_KEY_PREFIX);

/**
 * True when a tool has suspended and is waiting for this browser to answer it.
 *
 * Not the same as "incomplete": a tool call is incomplete from the moment its
 * arguments start streaming, long before the server has run it. Acting then
 * would submit a result for a call that does not exist yet, using arguments
 * that have not finished arriving. The suspension sigil on the *output* is the
 * only signal that the run has actually stopped and is waiting on us.
 */
export const isAwaitingBrowser = (output: unknown): boolean =>
  output !== null &&
  typeof output === 'object' &&
  (output as Record<string, unknown>)['@@cotera-tool-state'] === 'incomplete';

/** The `agentTool.id` a `client/fe-action/*` key was built from, or null. */
export const feActionIdFromToolKey = (toolKey: string): string | null =>
  isFeActionToolKey(toolKey)
    ? toolKey.slice(FE_ACTION_TOOL_KEY_PREFIX.length)
    : null;

/**
 * The stable id an action is exposed under, or null when it has none.
 *
 * `actionId` rather than the class name, because the class name does not
 * survive minification and the tool key has to mean the same thing across
 * deploys.
 */
export function agentActionId(action: Action): string | null {
  const declared = (action.constructor as Partial<ActionConstructor<never>>)
    .actionId;
  return declared !== undefined && AGENT_TOOL_ID_PATTERN.test(declared)
    ? declared
    : null;
}

/**
 * JSON Schema for an action's own input schema.
 *
 * Goes through the AI SDK's converter rather than zod's, because actions are
 * written against the v3 subpath and `z.toJSONSchema` only reads v4 schemas.
 */
export function toAgentJsonSchema(
  schema: Action['inputSchema']
): Record<string, unknown> {
  const json = zodSchema(schema).jsonSchema as Record<string, unknown>;
  // An empty `properties` map reads downstream as "any object at all" rather
  // than "no arguments", so zero-argument actions say so explicitly.
  const properties = json['properties'];
  const isEmptyObject =
    typeof properties === 'object' &&
    properties !== null &&
    Object.keys(properties).length === 0;
  if (properties === undefined || isEmptyObject) {
    return { type: 'object', properties: {}, required: [] };
  }
  return json;
}

/**
 * Every chat-scoped action that applies right now, as tool definitions.
 *
 * Built entirely from what the action already declares — its title, description
 * and input schema — so opting in is a scope, not a second contract to keep in
 * step with the first.
 *
 * An action that cannot be represented — no stable id, a schema that will not
 * convert — is dropped rather than thrown, because one malformed action must
 * not take the whole catalog down with it.
 */
export function buildFrontendActionCatalog(
  context: ApplicableContext & { getAllActions: () => Action[] }
): FrontendActionDefinition[] {
  const definitions: FrontendActionDefinition[] = [];

  for (const action of context.getAllActions()) {
    if (action.scopes?.includes('chat') !== true) {
      continue;
    }
    const id = agentActionId(action);
    if (id === null) {
      continue;
    }
    if (!action.applicable(context)) {
      continue;
    }

    let inputSchema: Record<string, unknown>;
    try {
      inputSchema = toAgentJsonSchema(action.inputSchema);
    } catch {
      continue;
    }

    const contextual = action.describeForAgent?.(context) ?? null;
    definitions.push({
      toolKey: feActionToolKey(id),
      name: action.title,
      description:
        contextual !== null && contextual !== ''
          ? contextual
          : action.description ?? action.title,
      inputSchema,
    });
  }

  return definitions;
}
